<template>
  <section class="hero-ui">
    <div class="hero-ui__bg" aria-hidden="true" />

    <div class="hero-ui__inner">
      <div class="hero-ui__top">
        <div class="hero-ui__copy">
          <h1 class="hero-ui__title">
            <span v-for="line in heroContent.titleLines" :key="line.text" class="hero-ui__title-line">
              <span :class="{ 'hero-ui__title-accent': line.accent }">{{ line.text }}</span>
            </span>
          </h1>

          <p class="hero-ui__desc">
            {{ heroContent.desc }}
          </p>

          <div class="hero-ui__actions">
            <a
              v-if="downloadHref"
              :href="downloadHref"
              class="gb-wave-btn gb-wave-btn--primary">
              <span class="hero-ui__btn-icon" aria-hidden="true" v-html="downloadIcon" />
              {{ heroContent.primaryCta }}
            </a>
            <button v-else type="button" class="gb-wave-btn gb-wave-btn--primary" @click="$emit('download')">
              <span class="hero-ui__btn-icon" aria-hidden="true" v-html="downloadIcon" />
              {{ heroContent.primaryCta }}
            </button>

            <router-link to="/old-versions/" class="gb-wave-btn gb-wave-btn--secondary">
              <span class="hero-ui__btn-icon" aria-hidden="true" v-html="historyIcon" />
              {{ heroContent.secondaryCta }}
            </router-link>
          </div>
        </div>

        <div class="hero-ui__visual">
          <div class="hero-ui__visual-stage">
            <span class="hero-ui__visual-ring hero-ui__visual-ring--one" />
            <span class="hero-ui__visual-ring hero-ui__visual-ring--two" />
            <span class="hero-ui__visual-ring hero-ui__visual-ring--three" />
            <img
              class="hero-ui__visual-logo"
              src="@/assets/gb-wave-logo.webp"
              alt="GB Wave — GB WhatsApp download"
              width="512"
              height="512">
          </div>
        </div>
      </div>

      <ul class="hero-ui__features">
        <li v-for="card in localizedFeatureCards" :key="card.title" class="hero-ui__feature">
          <span class="hero-ui__feature-icon" aria-hidden="true" v-html="card.iconSvg" />
          <strong>{{ card.title }}</strong>
          <span>{{ card.desc }}</span>
        </li>
      </ul>

      <article class="hero-ui__spec-card">
        <header class="hero-ui__spec-head">
          <h2 class="hero-ui__spec-title">
            {{ heroContent.specTitle }}
          </h2>
          <span class="hero-ui__spec-badge">
            <span aria-hidden="true" v-html="starIcon" />
            {{ heroContent.badge }}
          </span>
        </header>

        <ul class="hero-ui__spec-grid">
          <li v-for="item in localizedSpecItems" :key="item.label" class="hero-ui__spec-item">
            <span class="hero-ui__spec-icon" aria-hidden="true" v-html="item.iconSvg" />
            <span class="hero-ui__spec-label">{{ item.label }}</span>
            <strong class="hero-ui__spec-value">{{ item.value }}</strong>
          </li>
        </ul>

        <footer class="hero-ui__spec-foot">
          <div class="hero-ui__trust">
            <span class="hero-ui__trust-check" aria-hidden="true" v-html="checkBadgeIcon" />
            <span v-for="line in localizedTrustLines" :key="line" class="hero-ui__trust-item">
              {{ line }}
            </span>
          </div>

          <a
            v-if="downloadHref"
            :href="downloadHref"
            class="gb-wave-btn gb-wave-btn--primary hero-ui__spec-dl">
            <span class="hero-ui__btn-icon" aria-hidden="true" v-html="downloadIcon" />
            {{ heroContent.specCta }}
          </a>
          <button v-else type="button" class="gb-wave-btn gb-wave-btn--primary hero-ui__spec-dl" @click="$emit('download')">
            <span class="hero-ui__btn-icon" aria-hidden="true" v-html="downloadIcon" />
            {{ heroContent.specCta }}
          </button>
        </footer>
      </article>
    </div>
  </section>
</template>

<script>
import {
  APK_SIZE,
  APK_UPDATED,
  APK_VERSION,
  BRAND_GREEN,
  BRAND_GREEN_DARK,
} from '@/utlis/siteConfig';

function iconSvg(body, size = 24) {
  return `<svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" xmlns="http://www.w3.org/2000/svg">${body}</svg>`;
}

function lineIcon(body, size = 20) {
  return iconSvg(`<path ${body} stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />`, size);
}

function greenIcon(gradId, body, size = 30) {
  return iconSvg(
    `<defs><linearGradient id="${gradId}" x1="3" y1="3" x2="21" y2="21"><stop stop-color="${BRAND_GREEN}"/><stop offset="1" stop-color="${BRAND_GREEN_DARK}"/></linearGradient></defs>${body}`,
    size,
  );
}

const downloadIcon = lineIcon('d="M12 3v12m0 0l4-4m-4 4-4-4M5 19h14"');
const historyIcon = lineIcon('d="M12 8v5l3 2M4 12a8 8 0 1 0 2.35-5.65M4 4v5h5"');
const starIcon = iconSvg('<path d="M12 3.5l2.35 4.77 5.26.77-3.8 3.7.9 5.24L12 15.5l-4.71 2.48.9-5.24-3.8-3.7 5.26-.77L12 3.5z" fill="currentColor"/>', 13);
const checkBadgeIcon = iconSvg('<circle cx="12" cy="12" r="10" fill="currentColor"/><path d="M8 12.2l2.5 2.5L16 9" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>', 22);

const featureIcons = {
  shield: greenIcon(
    'hf-shield',
    '<path fill="url(#hf-shield)" d="M12 2.75l7.4 3.15v6.2c0 4.2-3.14 7.58-7.4 9.65-4.26-2.07-7.4-5.45-7.4-9.65V5.9L12 2.75z"/><path d="M8.9 12.1l2.05 2.05 4.5-4.7" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
  ),
  palette: greenIcon(
    'hf-palette',
    '<path fill="url(#hf-palette)" d="M12 3a9 9 0 1 0 0 18c1.48 0 2.15-.9 2.15-1.95 0-.62-.43-1.1-1.08-1.1H12c-1.12 0-2.08-.9-2.08-2.03 0-1.12.96-2.02 2.08-2.02h1.38A4.22 4.22 0 0 0 17.6 9.7C17.6 5.94 15.2 3 12 3z"/><circle cx="8.2" cy="10.4" r="1.15" fill="#fff"/><circle cx="11.75" cy="8" r="1.15" fill="#fff" opacity=".88"/><circle cx="15.4" cy="10.8" r="1.15" fill="#fff" opacity=".94"/>',
  ),
  chat: greenIcon(
    'hf-chat',
    '<path fill="url(#hf-chat)" d="M5.2 5.2h13.6c.88 0 1.6.72 1.6 1.6v7.9c0 .88-.72 1.6-1.6 1.6H9.3L5 19.6V6.8c0-.88.72-1.6 1.6-1.6z"/><path d="M8.6 10h6.8M8.6 12.7h4.8" stroke="#fff" stroke-width="1.7" stroke-linecap="round"/>',
  ),
  media: greenIcon(
    'hf-media',
    '<rect x="4" y="5.8" width="16" height="12.8" rx="2.4" fill="url(#hf-media)"/><circle cx="9" cy="10.2" r="1.3" fill="#fff"/><path fill="#fff" d="M5 16.8l4.7-4.1 2.85 2.55 2.45-2.05 4.1 3.6H5z"/>',
  ),
};

export default {
  name: 'HomeHero',
  props: {
    content: {
      type: Object,
      default: () => ({}),
    },
    downloadHref: {
      type: String,
      default: '',
    },
  },
  data() {
    const compactUpdated = APK_UPDATED.replace('y ', ' ');

    return {
      downloadIcon,
      historyIcon,
      starIcon,
      checkBadgeIcon,
      latestVersion: APK_VERSION,
      featureCards: [
        { iconSvg: featureIcons.shield, title: 'Anti-Ban', desc: 'Latest stable release' },
        { iconSvg: featureIcons.palette, title: 'Themes', desc: 'Full customization' },
        { iconSvg: featureIcons.chat, title: 'Auto Reply', desc: 'Smart messaging tools' },
        { iconSvg: featureIcons.media, title: 'HD Media', desc: 'Larger file sharing' },
      ],
      specItems: [
        {
          label: 'Version',
          value: `v${APK_VERSION}`,
          iconSvg: greenIcon('hs-version', '<path d="M5.2 8.2l6.8-4.6 6.8 4.6-6.8 4.65L5.2 8.2z" fill="url(#hs-version)"/><path d="M12 12.85v7.35M5.2 8.2v7.4L12 20.2l6.8-4.6V8.2" stroke="url(#hs-version)" stroke-width="2" stroke-linejoin="round"/>', 36),
        },
        {
          label: 'Android',
          value: '5.0+',
          iconSvg: greenIcon('hs-android', '<rect x="8" y="4" width="8" height="16" rx="2" fill="url(#hs-android)"/><circle cx="12" cy="17" r="1" fill="#fff"/>', 36),
        },
        {
          label: 'Size',
          value: APK_SIZE,
          iconSvg: greenIcon('hs-size', '<path d="M5 8h14v10.5H5V8z" fill="url(#hs-size)"/><path d="M8.5 8V6h7v2" stroke="#fff" stroke-width="1.6" stroke-linecap="round"/>', 36),
        },
        {
          label: 'Updated',
          value: compactUpdated,
          iconSvg: greenIcon('hs-date', '<rect x="4.5" y="6.2" width="15" height="13" rx="2" fill="url(#hs-date)"/><path d="M4.5 10.4h15M8.2 4.4v3.2M15.8 4.4v3.2" stroke="#fff" stroke-width="1.5" stroke-linecap="round"/>', 36),
        },
        {
          label: 'Verified',
          value: 'Yes',
          iconSvg: greenIcon('hs-verified', '<path fill="url(#hs-verified)" d="M12 2.75l7.4 3.15v6.2c0 4.2-3.14 7.58-7.4 9.65-4.26-2.07-7.4-5.45-7.4-9.65V5.9L12 2.75z"/><path d="M8.9 12.1l2.05 2.05 4.5-4.7" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>', 36),
        },
        {
          label: 'Platform',
          value: 'Android',
          iconSvg: greenIcon('hs-platform', '<circle cx="12" cy="12" r="8.2" fill="url(#hs-platform)"/><path d="M3.8 12h16.4M12 3.8c2.1 2.22 3.15 4.95 3.15 8.2S14.1 17.98 12 20.2M12 3.8C9.9 6.02 8.85 8.75 8.85 12S9.9 17.98 12 20.2" stroke="#fff" stroke-width="1.35"/>', 36),
        },
      ],
      trustLines: ['Safe download', 'Fast installation', 'Update guide included'],
    };
  },
  computed: {
    heroContent() {
      return {
        titleLines: [
          { text: 'GB WhatsApp APK' },
          { text: 'Latest Version', accent: true },
        ],
        desc:
          'Download the latest APK for Android with enhanced privacy, custom themes, auto reply, message scheduling, and larger media sharing - only on GB Wave.',
        primaryCta: 'Download APK',
        secondaryCta: 'View Old Versions',
        specTitle: 'Latest Version',
        badge: 'Recommended',
        specCta: 'Download Now',
        ...this.content,
      };
    },
    localizedFeatureCards() {
      const cards = this.content.featureCards || this.featureCards;
      return cards.map((card, index) => ({
        ...(this.featureCards[index] || {}),
        ...card,
        iconSvg: card.iconSvg || (this.featureCards[index] && this.featureCards[index].iconSvg),
      }));
    },
    localizedSpecItems() {
      if (!this.content.specLabels) return this.specItems;
      return this.specItems.map((item) => ({
        ...item,
        label: this.content.specLabels[item.label] || item.label,
        value: this.content.specValues && this.content.specValues[item.label]
          ? this.content.specValues[item.label]
          : item.value,
      }));
    },
    localizedTrustLines() {
      return this.content.trustLines || this.trustLines;
    },
  },
};
</script>

<style scoped lang="scss">
.hero-ui {
  position: relative;
  overflow: hidden;
  padding: 18px 0 48px;
  background: #f4fbf6;
  border-bottom: 0;
}

.hero-ui__bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    linear-gradient(105deg, #ffffff 0%, #f8fffa 38%, #ecf9f0 62%, #e2f5e9 100%);
}

.hero-ui__bg::before {
  position: absolute;
  top: 0;
  right: 0;
  width: 58%;
  height: 100%;
  background: radial-gradient(ellipse 85% 70% at 72% 42%, rgba(22, 196, 78, 0.14), transparent 68%);
  content: "";
}

.hero-ui::after {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  height: 120px;
  pointer-events: none;
  background: linear-gradient(180deg, rgba(244, 251, 246, 0), #f4fbf6 76%, #f6fbf8 100%);
  content: "";
  z-index: 0;
}

.hero-ui__inner {
  position: relative;
  z-index: 1;
  /* 上半区与下方白色规格卡共用同一内容宽 */
  max-width: 960px;
  margin: 0 auto;
  padding: 0 24px;
}

.hero-ui__top {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  align-items: start;
  gap: 16px;
  width: 100%;
  margin-bottom: 14px;
}

.hero-ui__copy {
  min-width: 0;
  padding-top: 0;
  align-self: center;
}

.hero-ui__title {
  max-width: none;
  margin: 0 0 12px;
  color: #07111f;
  font-size: clamp(2.2rem, 4.2vw, 3.1rem);
  font-weight: 900;
  line-height: 1.06;
  letter-spacing: -0.02em;
}

.hero-ui__title-line {
  display: block;
}

.hero-ui__title-accent {
  display: inline;
  color: #0ba83a;
  white-space: nowrap;
}

.hero-ui__desc {
  max-width: 34em;
  margin: 0 0 18px;
  color: #4a5568;
  font-size: 0.98rem;
  line-height: 1.55;
}

.hero-ui__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.hero-ui .gb-wave-btn {
  border-radius: 999px;
}

.gb-wave-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 0 26px;
  border: 1px solid transparent;
  border-radius: 999px;
  font-size: 0.95rem;
  font-weight: 800;
  cursor: pointer;
  text-decoration: none;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.gb-wave-btn:hover {
  transform: translateY(-1px);
}

.gb-wave-btn--primary {
  background: linear-gradient(180deg, #1fd058 0%, #0a9c36 100%);
  color: #fff;
  box-shadow: 0 10px 24px rgba(9, 157, 53, 0.26);
}

.gb-wave-btn--secondary {
  background: #fff;
  color: #0f9f3a;
  border-color: #1cb34a;
  box-shadow: 0 6px 14px rgba(12, 127, 48, 0.06);
}

.hero-ui__btn-icon {
  margin-right: 9px;
  color: currentColor;

  ::v-deep svg {
    display: block;
    width: 19px;
    height: 19px;
  }
}

.hero-ui__visual {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;
  align-self: center;
}

/* 仅圆环装饰，不加方形底框/描边 */
.hero-ui__visual-stage {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: min(100%, 320px);
  aspect-ratio: 1;
  background: transparent;
  border: 0;
  box-shadow: none;
}

.hero-ui__visual-ring {
  position: absolute;
  border: 1px solid rgba(17, 167, 61, 0.14);
  border-radius: 50%;
  pointer-events: none;
}

.hero-ui__visual-ring--one {
  inset: 0;
}

.hero-ui__visual-ring--two {
  inset: 12%;
}

.hero-ui__visual-ring--three {
  inset: 24%;
  border-color: rgba(17, 167, 61, 0.09);
}

.hero-ui__visual-logo {
  position: relative;
  z-index: 1;
  display: block;
  width: 82%;
  height: auto;
  border-radius: 36px;
  filter: drop-shadow(0 16px 24px rgba(8, 73, 30, 0.16));
}

.hero-ui__features {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 16px;
  padding: 14px 0 12px;
  margin: 0;
  list-style: none;
  border-top: 1px solid #dcebe3;
}

.hero-ui__feature {
  list-style: none;
  text-align: center;
}

.hero-ui__feature-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  margin: 0 auto 8px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.82);
  color: #0f9f3a;
  box-shadow: 0 10px 22px rgba(16, 154, 57, 0.09);

  ::v-deep svg {
    display: block;
    width: 34px;
    height: 34px;
    filter: drop-shadow(0 5px 9px rgba(14, 159, 58, 0.22));
  }
}

.hero-ui__feature strong {
  display: block;
  margin-bottom: 2px;
  color: #141a22;
  font-size: 0.92rem;
  font-weight: 900;
  line-height: 1.2;
}

.hero-ui__feature span:last-child {
  color: #6b7280;
  font-size: 0.76rem;
  line-height: 1.35;
}

.hero-ui__spec-card {
  padding: 12px 16px 12px;
  border: 1px solid #d5e8dc;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 10px 28px rgba(24, 76, 43, 0.06);
}

.hero-ui__spec-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
}

.hero-ui__spec-title {
  margin: 0;
  color: #111722;
  font-size: 0.98rem;
  font-weight: 900;
  line-height: 1.2;
}

.hero-ui__spec-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 26px;
  padding: 4px 10px;
  border-radius: 999px;
  background: linear-gradient(180deg, #18bd4b, #0ca439);
  color: #fff;
  font-size: 0.68rem;
  font-weight: 800;
  white-space: nowrap;

  ::v-deep svg {
    display: block;
    width: 13px;
    height: 13px;
  }
}

.hero-ui__spec-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  overflow: hidden;
  margin: 0;
  padding: 0;
  list-style: none;
  border: 1px solid #e1eee6;
  border-radius: 8px;
  background: rgba(253, 255, 253, 0.88);
}

.hero-ui__spec-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 118px;
  padding: 15px 8px 13px;
  list-style: none;
  text-align: center;
  border-right: 1px solid #e1eee6;
  border-bottom: 1px solid #e1eee6;
}

.hero-ui__spec-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 54px;
  height: 54px;
  margin-bottom: 8px;
  border: 1px solid rgba(22, 189, 75, 0.22);
  border-radius: 8px;
  background: linear-gradient(145deg, rgba(235, 251, 240, 0.96), rgba(255, 255, 255, 0.98));
  box-shadow: 0 12px 24px rgba(18, 151, 58, 0.12);

  ::v-deep svg {
    display: block;
    width: 36px;
    height: 36px;
  }
}

.hero-ui__spec-label {
  display: block;
  margin-bottom: 1px;
  color: #0f9f3a;
  font-size: 0.76rem;
  font-weight: 800;
  line-height: 1.15;
}

.hero-ui__spec-value {
  display: block;
  color: #111722;
  font-size: 0.98rem;
  font-weight: 900;
  line-height: 1.15;
}

.hero-ui__spec-foot {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  padding-top: 8px;
}

.hero-ui__trust {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 6px;
  color: #1d2938;
  font-size: 0.82rem;
  font-weight: 700;
}

.hero-ui__trust-check {
  display: inline-flex;
  margin-right: 4px;
  color: #13ac41;

  ::v-deep svg {
    display: block;
    width: 18px;
    height: 18px;
  }
}

.hero-ui__trust-item:not(:last-child)::after {
  content: " •";
  color: #94a3b8;
  font-weight: 400;
}

.hero-ui__spec-dl {
  align-self: stretch;
  min-width: 200px;
  min-height: 44px;
  padding: 0 22px;
  font-size: 0.94rem;
}

@media (min-width: 560px) {
  .hero-ui__visual {
    min-height: 0;
  }

  .hero-ui__features {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
    padding: 14px 0 12px;
  }

  .hero-ui__spec-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (min-width: 700px) {
  .hero-ui__top {
    /* 左右贴齐内容区，与下方白色卡同宽对齐 */
    display: flex;
    flex-wrap: nowrap;
    justify-content: space-between;
    align-items: center;
    gap: 20px;
    width: 100%;
  }

  .hero-ui__copy {
    flex: 1 1 auto;
    min-width: 0;
    max-width: none;
  }

  .hero-ui__visual {
    flex: 0 0 280px;
    width: 280px;
    max-width: 38%;
    justify-content: flex-end;
    align-items: center;
  }

  .hero-ui__visual-stage {
    width: 100%;
  }

  .hero-ui__visual-logo {
    width: 84%;
  }
}

@media (min-width: 960px) {
  .hero-ui__top {
    gap: 24px;
  }

  .hero-ui__visual {
    flex-basis: 300px;
    width: 300px;
  }

  .hero-ui__visual-stage {
    width: 100%;
  }

  .hero-ui__spec-grid {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }

  .hero-ui__spec-item {
    border-bottom: 0;
  }

  .hero-ui__spec-foot {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }

  .hero-ui__spec-dl {
    align-self: center;
  }
}

@media (min-width: 1280px) {
  .hero-ui__inner {
    max-width: 960px;
  }

  .hero-ui__top {
    gap: 28px;
  }

  .hero-ui__visual {
    flex-basis: 320px;
    width: 320px;
  }

  .hero-ui__visual-stage {
    width: 100%;
  }

  .hero-ui__visual-logo {
    width: 82%;
  }

  .hero-ui__title {
    font-size: clamp(2.45rem, 2.8vw, 3.1rem);
  }

  .hero-ui__desc {
    font-size: 1rem;
  }
}

@media (max-width: 699px) {
  .hero-ui {
    padding-top: 14px;
    padding-bottom: 22px;
  }

  .hero-ui__inner {
    padding: 0 16px;
  }

  .hero-ui__bg::before {
    top: 34%;
    right: -42%;
    width: 105%;
    height: 68%;
    opacity: 0.78;
  }

  .hero-ui__title {
    font-size: clamp(1.86rem, 6vw, 2.42rem);
    line-height: 1.06;
  }

  .hero-ui__desc {
    font-size: 1rem;
  }

  .hero-ui__visual {
    margin-top: 0;
  }

  .hero-ui__visual-stage {
    width: min(92vw, 320px);
  }

  .gb-wave-btn {
    flex: 1 1 190px;
    padding: 0 16px;
  }

  .hero-ui__spec-card {
    padding: 10px 12px 10px;
  }

  .hero-ui__features {
    padding: 7px 0 9px;
  }
}

@media (max-width: 559px) {
  .hero-ui__title {
    font-size: clamp(1.72rem, 8.1vw, 2.28rem);
  }

  .hero-ui__title-accent {
    margin-left: 0.12em;
  }
}

@media (max-width: 374px) {
  .hero-ui__title {
    font-size: clamp(1.6rem, 8vw, 1.9rem);
  }
}
</style>
